import { DEFAULT_QUERY_OPTIONS } from "#src/components/hooks/adapters/adapterUtils";
import { useAssistant } from "#src/contexts/Assistant";
import { ExceptionUtils } from "#src/utils/exception";
import { useQueryClient } from "@tanstack/react-query";
import {
  Resources,
  WorkflowAdapter,
  WorkflowPrompts,
  WorkflowPromptsType,
} from "@validereinc/domain";
import React from "react";
import { AssistantPrompt } from "../AssistantPrompt";
import { useLocation } from "react-router-dom";

export const WorkflowPromptConfigurations: Record<
  WorkflowPromptsType,
  { prompt: string; loadingText: string }
> = {
  [WorkflowPrompts.SUMMARY]: {
    prompt: "Summarize the workflow",
    loadingText: "Summarizing the workflow for you, just a minute...",
  },
  [WorkflowPrompts.LONGEST_TASK]: {
    prompt: "What task took the longest to complete?",
    loadingText: "Calculating the longest task, just a minute...",
  },
  [WorkflowPrompts.USER_SUMMARY]: {
    prompt: "Which users completed tasks?",
    loadingText: "Finding the users who completed tasks, just a minute...",
  },
  [WorkflowPrompts.TOTAL_DURATION]: {
    prompt: "What is the total duration of the workflow?",
    loadingText: "Calculating the workflow duration, just a minute...",
  },
};

export const WorkflowPrompt = ({
  workflowId,
  promptId,
}: {
  workflowId: string;
  promptId: WorkflowPromptsType;
}) => {
  const queryClient = useQueryClient();
  const { setLoadingStateLabel, setContentAsync, clearContent } =
    useAssistant();
  const { pathname } = useLocation();

  const { prompt, loadingText } = WorkflowPromptConfigurations[promptId];

  return (
    <AssistantPrompt
      onClick={() => {
        setLoadingStateLabel(loadingText);
        setContentAsync(
          async () => {
            try {
              const summary = await queryClient.fetchQuery({
                queryKey: [Resources.WORKFLOW, workflowId, "summary", promptId],
                queryFn: async () => {
                  const resp = await WorkflowAdapter.summary.getOne({
                    id: workflowId,
                    meta: { promptChoice: promptId },
                  });

                  return resp.message;
                },
                ...DEFAULT_QUERY_OPTIONS,
              });

              return summary;
            } catch (err) {
              ExceptionUtils.reportException(err, "error", {
                hint: `Assistant content failed to fetch for ${promptId}`,
              });
              throw err;
            }
          },
          {
            isTiedToCurrentLocation: true,
            location: pathname,
            contentFooter: (
              <>
                <h6>How else can I help?</h6>
                {Object.values(WorkflowPrompts)
                  .filter((value) => value !== promptId)
                  .map((promptId) => (
                    <WorkflowPrompt
                      key={promptId}
                      workflowId={workflowId}
                      promptId={promptId}
                    />
                  ))}
                <AssistantPrompt
                  onClick={() => {
                    clearContent();
                  }}
                >
                  That&#39;s all, thanks!
                </AssistantPrompt>
              </>
            ),
          }
        );
      }}
    >
      {prompt}
    </AssistantPrompt>
  );
};
